import React, { useState } from 'react';
import { Form, Button, Row, Col, Card, Badge } from 'react-bootstrap';
import { FaPlus, FaTrash, FaTractor } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { getEquipmentTypeOptions } from '../../utils/equipmentList';

// Custom styles for form labels
const labelStyle = {
  fontWeight: '600',
  color: '#495057',
  marginBottom: '0.25rem',
  fontSize: '0.9rem'
};

// Section heading styles
const sectionHeadingStyle = {
  fontSize: '0.9rem',
  fontWeight: '600',
  marginBottom: '0.3rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
};

/**
 * EquipmentInformation Component
 * 
 * Handles equipment information for equipment financing.
 * Allows adding multiple equipment items with details.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.equipmentItems - Array of equipment items
 * @param {Function} props.setEquipmentItems - Function to update equipment items
 * @param {boolean} props.disabled - Whether the form is disabled
 * @returns {JSX.Element} EquipmentInformation component
 */
const EquipmentInformation = ({ 
  equipmentItems = [], 
  setEquipmentItems,
  disabled = false
}) => {
  
  // Equipment condition options
  const conditionOptions = [
    { value: "new", label: "New" },
    { value: "used", label: "Used" },
    { value: "refurbished", label: "Refurbished" }
  ];
  
  // Get equipment types from shared equipment list
  const equipmentTypes = [
    { value: "", label: "Select equipment type" },
    ...getEquipmentTypeOptions()
  ];
  
  /**
   * Add a new equipment item
   */
  const addEquipmentItem = () => {
    if (disabled) return;
    
    const newItem = {
      condition: "new",
      equipmentType: "",
      numberOfUnits: 1,
      costPerUnit: "",
      titled: false
    };
    
    setEquipmentItems([...equipmentItems, newItem]);
  };
  
  /**
   * Remove an equipment item
   * 
   * @param {number} index - Index of the item to remove
   */
  const removeEquipmentItem = (index) => {
    if (disabled) return;
    
    const updatedItems = equipmentItems.filter((_, i) => i !== index);
    setEquipmentItems(updatedItems);
  };
  
  /**
   * Update a specific field in an equipment item
   * 
   * @param {number} index - Index of the equipment item
   * @param {string} field - Field name to update
   * @param {any} value - New value
   */
  const updateEquipmentItem = (index, field, value) => {
    if (disabled) return;
    
    const updatedItems = [...equipmentItems];
    
    // Process numeric fields
    if (field === 'numberOfUnits') {
      // Ensure it's an integer
      value = parseInt(value) || 1;
      // Minimum of 1 unit
      value = Math.max(1, value);
    } else if (field === 'costPerUnit') {
      // Remove non-numeric characters except decimal point
      value = value.replace(/[^\d.]/g, '');
      // Ensure only one decimal point
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
    }
    
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setEquipmentItems(updatedItems);
  };
  
  /**
   * Calculate the total cost of an equipment item
   * 
   * @param {Object} item - Equipment item
   * @returns {number} Total cost
   */
  const calculateItemTotal = (item) => {
    const units = parseInt(item.numberOfUnits) || 0;
    const cost = parseFloat(item.costPerUnit) || 0;
    return units * cost;
  };
  
  /**
   * Calculate the total cost of all equipment items
   * 
   * @returns {number} Total cost
   */
  const calculateTotalCost = () => {
    return equipmentItems.reduce((total, item) => total + calculateItemTotal(item), 0);
  };
  
  /**
   * Format currency value
   * 
   * @param {number} value - Value to format
   * @returns {string} Formatted value
   */
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };
  
  return (
    <div className="equipment-information-container mb-4">
      <div className="section-header mb-2">
        <h5 className="section-title">
          Equipment Information
          <Badge bg="info" className="ms-2" pill>
            <span style={{fontSize: '0.8rem'}}>{equipmentItems.length} {equipmentItems.length === 1 ? 'item' : 'items'}</span>
          </Badge>
        </h5>
      </div>
      
      {equipmentItems.length === 0 ? (
        <Card className="p-3 mb-3 text-center bg-light">
          <p className="mb-2">No equipment items added yet.</p>
          <Button 
            variant="primary" 
            onClick={addEquipmentItem}
            disabled={disabled}
            className="mx-auto"
            style={{ width: 'fit-content' }}
          >
            <FaPlus className="me-1" size="0.8em" /> Add Equipment
          </Button>
        </Card>
      ) : (
        <>
          {equipmentItems.map((item, index) => (
            <Card key={index} className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center bg-light py-2">
                <div>
                  <FaTractor className="me-2" />
                  <span>Equipment #{index + 1}</span>
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => removeEquipmentItem(index)}
                  disabled={disabled}
                >
                  <FaTrash size="0.8em" />
                </Button>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>
                        Condition <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={item.condition}
                        onChange={(e) => updateEquipmentItem(index, 'condition', e.target.value)}
                        disabled={disabled}
                      >
                        {conditionOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>
                        Equipment type <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={item.equipmentType}
                        onChange={(e) => updateEquipmentItem(index, 'equipmentType', e.target.value)}
                        disabled={disabled}
                      >
                        {equipmentTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>
                        Number of units <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={item.numberOfUnits}
                        onChange={(e) => updateEquipmentItem(index, 'numberOfUnits', e.target.value)}
                        disabled={disabled}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>
                        Cost per unit <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={item.costPerUnit}
                        onChange={(e) => updateEquipmentItem(index, 'costPerUnit', e.target.value)}
                        placeholder="$"
                        disabled={disabled}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>
                        Titled?
                      </Form.Label>
                      <div>
                        <Form.Check
                          type="checkbox"
                          id={`titled-${index}`}
                          label="Yes, this equipment is titled"
                          checked={item.titled}
                          onChange={(e) => updateEquipmentItem(index, 'titled', e.target.checked)}
                          disabled={disabled}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                <div className="text-end mt-2">
                  <div style={{fontWeight: 'bold'}}>
                    Item Total: {formatCurrency(calculateItemTotal(item))}
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
          
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button 
              variant="outline-primary" 
              onClick={addEquipmentItem}
              disabled={disabled}
            >
              <FaPlus className="me-1" size="0.8em" /> Add Another Equipment Item
            </Button>
            
            <div style={{fontWeight: 'bold', fontSize: '1.1rem'}}>
              Total Cost: {formatCurrency(calculateTotalCost())}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

EquipmentInformation.propTypes = {
  equipmentItems: PropTypes.array,
  setEquipmentItems: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

export default EquipmentInformation; 